/**
 * Project CSV import — column spec, header mapping, and row normalization.
 *
 * Shared by the admin import modal (preview / client-side validation) and the
 * server import endpoint (authoritative validation + insert). Keep this file
 * dependency-free so it compiles under both tsconfigs.
 */

import { PROJECT_CATEGORIES, TABS, ALL_TABS } from './project';
import type { ProjectCategory, TabId } from './project';

/** Hard cap per import request. */
export const PROJECT_IMPORT_MAX_ROWS = 500;

/** Separator between multiple links / tabs inside one CSV cell (pipe and newline also accepted). */
export const PROJECT_IMPORT_LIST_SEPARATOR = ',';

export interface ProjectImportColumnSpec {
  key: ProjectImportColumnKey;
  /** Canonical header written by the template. */
  header: string;
  /** Accepted alternative headers (already normalized: lowercase, underscores). */
  aliases: string[];
  description: string;
  example: string;
}

export type ProjectImportColumnKey =
  | 'name'
  | 'client'
  | 'end_client'
  | 'site_number'
  | 'address'
  | 'city'
  | 'state'
  | 'category'
  | 'description'
  | 'matterport_urls'
  | 'autodesk_urls'
  | 'enabled_tabs'
  | 'latitude'
  | 'longitude';

export const PROJECT_IMPORT_COLUMNS: ProjectImportColumnSpec[] = [
  {
    key: 'name', header: 'name', aliases: ['project_name', 'project', 'title'],
    description: 'Optional. Leave blank to auto-generate "Client Site - City, State" from the other fields.',
    example: '',
  },
  {
    key: 'client', header: 'client', aliases: ['client_name', 'organization', 'org', 'org_code', 'client_code', 'company'],
    description: 'Client organization: 6-digit org code, exact name, or slug. Unmatched values are kept as text only.',
    example: 'AutoZone',
  },
  {
    key: 'end_client', header: 'end_client', aliases: ['endclient', 'end_client_name', 'tenant'],
    description: 'Optional end client shown in the portal.',
    example: 'Store Development',
  },
  {
    key: 'site_number', header: 'site_number', aliases: ['site', 'site_no', 'site_num', 'store_number', 'store', 'store_no', 'store_num'],
    description: 'Store or site number.',
    example: '11446',
  },
  { key: 'address', header: 'address', aliases: ['street', 'street_address', 'address1', 'address_1'], description: 'Street address.', example: '123 Main St' },
  { key: 'city', header: 'city', aliases: ['town'], description: 'City.', example: 'Almont' },
  { key: 'state', header: 'state', aliases: ['st', 'province', 'region'], description: 'State (2-letter or full).', example: 'MI' },
  {
    key: 'category', header: 'category', aliases: ['industry', 'industry_category', 'project_category', 'type'],
    description: 'One of: ' + PROJECT_CATEGORIES.map(c => c.id).join(', ') + '. Blank = other.',
    example: 'retail',
  },
  { key: 'description', header: 'description', aliases: ['notes', 'desc', 'comments'], description: 'Free text.', example: 'Existing-conditions scan, phase 1' },
  {
    key: 'matterport_urls', header: 'matterport_urls', aliases: ['matterport', 'matterport_url', 'tours', 'tour_urls', 'virtual_tours', 'virtual_tour'],
    description: 'One or more Matterport links separated by commas. Optional label with "Label=URL".',
    example: 'Interior=https://my.matterport.com/show/?m=abc123, Roof=https://my.matterport.com/show/?m=def456',
  },
  {
    key: 'autodesk_urls', header: 'autodesk_urls', aliases: ['autodesk', 'autodesk_url', 'model_urls', 'model_url', 'bim_urls', 'bim_url', 'models', 'bim'],
    description: 'One or more Autodesk viewer links separated by commas. Optional label with "Label=URL".',
    example: 'Revit Model=https://a360.co/4apZc0O',
  },
  {
    key: 'enabled_tabs', header: 'enabled_tabs', aliases: ['tabs', 'deliverables'],
    description: 'Tabs to show, separated by commas: ' + ALL_TABS.join(', ') + '. Blank = inferred from the links provided.',
    example: 'matterport, model, pointcloud',
  },
  { key: 'latitude', header: 'latitude', aliases: ['lat'], description: 'Optional decimal latitude.', example: '42.9203' },
  { key: 'longitude', header: 'longitude', aliases: ['lng', 'lon', 'long'], description: 'Optional decimal longitude.', example: '-83.0449' },
];

/** Raw row as parsed from CSV, keyed by canonical column key. */
export type ProjectImportRow = Partial<Record<ProjectImportColumnKey, string>>;

export interface ProjectImportLink {
  name: string;
  url: string;
}

/** Row after normalization; `errors` non-empty means the row cannot be imported. */
export interface NormalizedProjectImportRow {
  rowNumber: number;
  name?: string;
  clientRef?: string;
  endClient?: string;
  siteNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  category: ProjectCategory;
  description?: string;
  matterportTours: ProjectImportLink[];
  autodeskModels: ProjectImportLink[];
  enabledTabs: TabId[];
  latitude?: number;
  longitude?: number;
  errors: string[];
  warnings: string[];
}

export interface ProjectImportRowResult {
  rowNumber: number;
  ok: boolean;
  projectId?: string;
  /** Final project name (auto-generated or provided). */
  name?: string;
  /** Resolved client organization name (existing, or the one that will be created). */
  clientName?: string;
  /** True when the row's client did not exist and will be created by the import. */
  willCreateClient?: boolean;
  errors: string[];
  warnings: string[];
}

export interface ProjectImportResponse {
  dryRun: boolean;
  total: number;
  created: number;
  failed: number;
  results: ProjectImportRowResult[];
  /** Distinct new clients the import would create (dry run) / did create. */
  clientsToCreate?: number;
  clientsCreated?: number;
}

// ─── Header mapping ──────────────────────────────────────────────────────────

/** Strip a UTF-8 byte-order mark (U+FEFF) from the start of a string. */
function stripBom(value: string): string {
  return value.charCodeAt(0) === 0xfeff ? value.slice(1) : value;
}

/** Lowercase, strip BOM, collapse non-alphanumerics to underscores. */
export function normalizeHeader(header: string): string {
  return stripBom(header)
    .trim()
    .toLowerCase()
    .replace(/[#]/g, '_num')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Map CSV headers to canonical column keys. Returns null for unrecognized
 * headers and lists them so the UI can warn.
 */
export function mapImportHeaders(headers: string[]): {
  mapping: Array<ProjectImportColumnKey | null>;
  unknown: string[];
  missingRequired: string[];
} {
  const lookup = new Map<string, ProjectImportColumnKey>();
  for (const col of PROJECT_IMPORT_COLUMNS) {
    lookup.set(col.header, col.key);
    for (const a of col.aliases) lookup.set(a, col.key);
  }
  const mapping: Array<ProjectImportColumnKey | null> = [];
  const unknown: string[] = [];
  const seen = new Set<ProjectImportColumnKey>();
  for (const h of headers) {
    const key = lookup.get(normalizeHeader(h)) ?? null;
    if (key && !seen.has(key)) {
      mapping.push(key);
      seen.add(key);
    } else {
      mapping.push(null);
      if (h.trim()) unknown.push(h.trim());
    }
  }
  // Nothing is strictly required, but a file with no recognized columns is useless.
  const missingRequired = seen.size === 0 ? ['at least one recognized column'] : [];
  return { mapping, unknown, missingRequired };
}

/** Convert a parsed CSV table into import rows using a header mapping. Blank rows are dropped. */
export function importRowsFromTable(
  mapping: Array<ProjectImportColumnKey | null>,
  records: string[][],
): ProjectImportRow[] {
  const rows: ProjectImportRow[] = [];
  for (const record of records) {
    const row: ProjectImportRow = {};
    let hasValue = false;
    record.forEach((cell, i) => {
      const key = mapping[i];
      if (!key) return;
      const value = (cell ?? '').trim();
      if (value) {
        row[key] = value;
        hasValue = true;
      }
    });
    if (hasValue) rows.push(row);
  }
  return rows;
}

// ─── Value parsing ───────────────────────────────────────────────────────────

function splitList(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split(/\r?\n|\||,/)
    .map(s => s.trim())
    .filter(Boolean);
}

/**
 * Parse "Label=URL" or bare URL entries. A bare URL gets `${defaultName} N`
 * when there is more than one, otherwise `defaultName`.
 */
export function parseImportLinks(value: string | undefined, defaultName: string): { links: ProjectImportLink[]; errors: string[] } {
  const items = splitList(value);
  const links: ProjectImportLink[] = [];
  const errors: string[] = [];
  items.forEach((item, i) => {
    let name = '';
    let url = item;
    const eq = item.indexOf('=');
    // Only treat "=" as a label separator when it appears before the scheme.
    if (eq > 0 && !/^https?:\/\//i.test(item)) {
      name = item.slice(0, eq).trim();
      url = item.slice(eq + 1).trim();
    }
    if (!/^https?:\/\/\S+$/i.test(url)) {
      errors.push(`${defaultName} link ${i + 1} is not a valid http(s) URL: "${url}"`);
      return;
    }
    links.push({ name: name || (items.length > 1 ? `${defaultName} ${i + 1}` : defaultName), url });
  });
  return { links, errors };
}

const CATEGORY_LOOKUP: Record<string, ProjectCategory> = (() => {
  const m: Record<string, ProjectCategory> = {};
  for (const c of PROJECT_CATEGORIES) {
    m[c.id.toLowerCase()] = c.id;
    m[c.label.toLowerCase()] = c.id;
  }
  // Friendly aliases
  m['church'] = 'religious';
  m['churches'] = 'religious';
  m['worship'] = 'religious';
  m['house of worship'] = 'religious';
  m['religion'] = 'religious';
  m['faith'] = 'religious';
  m['medical'] = 'healthcare';
  m['hospital'] = 'healthcare';
  m['school'] = 'education';
  m['hotel'] = 'hospitality';
  m['restaurant'] = 'hospitality';
  m['warehouse'] = 'industrial';
  m['office'] = 'commercial';
  m['municipal'] = 'government';
  return m;
})();

const TAB_LOOKUP: Record<string, TabId> = (() => {
  const m: Record<string, TabId> = {};
  for (const t of TABS) {
    m[t.id.toLowerCase()] = t.id;
    m[t.label.toLowerCase()] = t.id;
  }
  // Friendly extras
  m['tour'] = 'matterport';
  m['virtual tour'] = 'matterport';
  m['bim'] = 'model';
  m['3d model'] = 'model';
  m['point cloud'] = 'pointcloud';
  m['map'] = 'geotiff';
  m['ortho'] = 'geotiff';
  m['orthomosaic'] = 'geotiff';
  return m;
})();

function parseNumber(value: string | undefined, label: string, min: number, max: number, errors: string[]): number | undefined {
  if (value === undefined || value === '') return undefined;
  const n = Number(value);
  if (!Number.isFinite(n) || n < min || n > max) {
    errors.push(`${label} must be a number between ${min} and ${max} (got "${value}")`);
    return undefined;
  }
  return n;
}

function clip(value: string | undefined, max: number): string | undefined {
  if (!value) return undefined;
  const v = value.trim();
  return v.length > max ? v.slice(0, max) : v;
}

// ─── Row normalization ───────────────────────────────────────────────────────

/**
 * Validate and normalize one CSV row. Never throws; problems land in
 * `errors` (blocking) or `warnings` (informational).
 */
export function normalizeProjectImportRow(row: ProjectImportRow, rowNumber: number): NormalizedProjectImportRow {
  const errors: string[] = [];
  const warnings: string[] = [];

  const name = clip(row.name, 200);
  const clientRef = clip(row.client, 200);
  const endClient = clip(row.end_client, 200);
  const siteNumber = clip(row.site_number, 50);
  const address = clip(row.address, 300);
  const city = clip(row.city, 100);
  const state = clip(row.state, 50);
  const description = clip(row.description, 5000);

  let category: ProjectCategory = 'other';
  if (row.category) {
    const c = CATEGORY_LOOKUP[row.category.trim().toLowerCase()];
    if (c) category = c;
    else errors.push(`Unknown category "${row.category}". Use one of: ${PROJECT_CATEGORIES.map(x => x.id).join(', ')}`);
  }

  const tours = parseImportLinks(row.matterport_urls, 'Tour');
  errors.push(...tours.errors);
  for (const t of tours.links) {
    if (!/matterport\.com/i.test(t.url)) warnings.push(`Tour "${t.name}" is not a matterport.com link`);
  }

  const models = parseImportLinks(row.autodesk_urls, 'Model');
  errors.push(...models.errors);

  const enabledTabs: TabId[] = [];
  if (row.enabled_tabs) {
    for (const item of splitList(row.enabled_tabs)) {
      const tab = TAB_LOOKUP[item.toLowerCase()];
      if (!tab) errors.push(`Unknown tab "${item}". Use one of: ${ALL_TABS.join(', ')}`);
      else if (!enabledTabs.includes(tab)) enabledTabs.push(tab);
    }
  } else {
    if (tours.links.length) enabledTabs.push('matterport');
    if (models.links.length) enabledTabs.push('model');
  }

  const latitude = parseNumber(row.latitude, 'latitude', -90, 90, errors);
  const longitude = parseNumber(row.longitude, 'longitude', -180, 180, errors);
  if (!!row.latitude !== !!row.longitude) {
    errors.push('latitude and longitude must be provided together');
  }

  if (!name && !clientRef && !siteNumber && !address && !city && !state) {
    if (!description && !tours.links.length && !models.links.length) {
      errors.push('Row is empty');
    } else {
      warnings.push('No name, client, or location; project will be called "New Project"');
    }
  }

  return {
    rowNumber,
    name, clientRef, endClient, siteNumber, address, city, state,
    category, description,
    matterportTours: tours.links,
    autodeskModels: models.links,
    enabledTabs,
    latitude, longitude,
    errors, warnings,
  };
}

// ─── Template ────────────────────────────────────────────────────────────────

function csvEscape(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Header row plus one example row, ready to download. */
export function buildProjectImportTemplate(): string {
  const header = PROJECT_IMPORT_COLUMNS.map(c => c.header).join(',');
  const example = PROJECT_IMPORT_COLUMNS.map(c => csvEscape(c.example)).join(',');
  const second = PROJECT_IMPORT_COLUMNS.map(c => {
    switch (c.key) {
      case 'client': return 'Type Six';
      case 'end_client': return '';
      case 'site_number': return '';
      case 'address': return '12801 West Sunrise Boulevard';
      case 'city': return 'Sunrise';
      case 'state': return 'FL';
      case 'category': return 'commercial';
      case 'description': return csvEscape('Full scan, no site number');
      default: return '';
    }
  }).join(',');
  return `${header}\r\n${example}\r\n${second}\r\n`;
}
